// @ts-nocheck
// ============================================
// 导出对话框组件
// 功能：导出标注为Markdown/JSON/CSV格式
// ============================================

import React, { useState } from 'react';
import { exportAnnotations, BLOCK_TYPES } from './pdfUtils';
import './ExportDialog.css';

type ExportFormat = 'markdown' | 'json' | 'csv';
type SessionType = 'guide' | 'qa' | 'manual';

// 映射 sessionType 到 blockTypes
const SESSION_TYPE_TO_BLOCK_TYPES: Record<SessionType, string[]> = {
  guide: [
    BLOCK_TYPES.QA_QUICK_SCAN,
    BLOCK_TYPES.QA_DEEP_DIVE,
    BLOCK_TYPES.QA_CRITICAL,
    BLOCK_TYPES.QA_SOCRATIC,
  ],
  qa: [BLOCK_TYPES.QA_NORMAL],
  manual: [BLOCK_TYPES.ANNOTATION],
};

interface PDFInfoData {
  fileName: string;
  fileSize: number;
  totalPages: number;
}

interface ExportOptions {
  sessionType: SessionType | null;
  includeDeleted: boolean;
}

interface ExportDialogProps {
  show: boolean;
  pdfFingerprint: string;
  pdfInfo: PDFInfoData | null;
  onClose: () => void;
}

const ExportDialog: React.FC<ExportDialogProps> = ({ show, pdfFingerprint, pdfInfo, onClose }) => {
  const [exportFormat, setExportFormat] = useState<ExportFormat>('markdown');
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    sessionType: null,
    includeDeleted: false
  });
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setError(null);

    try {
      const blockTypes = exportOptions.sessionType
        ? SESSION_TYPE_TO_BLOCK_TYPES[exportOptions.sessionType]
        : undefined;

      const result = await exportAnnotations({
        fingerprint: pdfFingerprint,
        format: exportFormat,
        blockTypes: blockTypes || [],
        includeDeleted: exportOptions.includeDeleted
      }) as any;

      if (result.success) {
        const timestamp = new Date().toISOString().slice(0, 10);
        const extensionMap: Record<ExportFormat, string> = { markdown: 'md', json: 'json', csv: 'csv' };
        const extension = extensionMap[exportFormat];
        const filename = `学习笔记_${timestamp}.${extension}`;

        const content = exportFormat === 'json'
          ? JSON.stringify(result.data, null, 2)
          : result.content;

        downloadFile(content, filename);
        onClose();
      } else {
        throw new Error(result.error || '导出失败');
      }
    } catch (err: any) {
      console.error('导出失败:', err);
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const downloadFile = (content: string, filename: string) => {
    let mimeType = 'text/plain;charset=utf-8';
    if (filename.endsWith('.json')) {
      mimeType = 'application/json;charset=utf-8';
    } else if (filename.endsWith('.csv')) {
      mimeType = 'text/csv;charset=utf-8';
    } else if (filename.endsWith('.md')) {
      mimeType = 'text/markdown;charset=utf-8';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const toggleOption = <K extends keyof ExportOptions>(option: K, value: ExportOptions[K]) => {
    setExportOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };

  if (!show) return null;

  return (
    <div className="export-overlay" onClick={onClose}>
      <div className="export-dialog" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <div className="export-header">
          <h3>📤 导出标注</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="export-body">
          {pdfInfo && (
            <div className="export-pdf-info">
              <div className="pdf-name">{pdfInfo.fileName}</div>
              <div className="pdf-details">
                {Math.round(pdfInfo.fileSize / 1024)}KB · {pdfInfo.totalPages}页
              </div>
            </div>
          )}

          <div className="export-section">
            <h4>导出格式</h4>
            <div className="format-options">
              <label className={`format-option ${exportFormat === 'markdown' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  value="markdown"
                  checked={exportFormat === 'markdown'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExportFormat(e.target.value as ExportFormat)}
                />
                <div className="format-content">
                  <div className="format-icon">📝</div>
                  <div className="format-info">
                    <div className="format-name">Markdown</div>
                    <div className="format-desc">适合阅读和二次编辑</div>
                  </div>
                </div>
              </label>

              <label className={`format-option ${exportFormat === 'json' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  value="json"
                  checked={exportFormat === 'json'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExportFormat(e.target.value as ExportFormat)}
                />
                <div className="format-content">
                  <div className="format-icon">📊</div>
                  <div className="format-info">
                    <div className="format-name">JSON</div>
                    <div className="format-desc">结构化数据，便于程序处理</div>
                  </div>
                </div>
              </label>

              <label className={`format-option ${exportFormat === 'csv' ? 'active' : ''}`}>
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={exportFormat === 'csv'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExportFormat(e.target.value as ExportFormat)}
                />
                <div className="format-content">
                  <div className="format-icon">📈</div>
                  <div className="format-info">
                    <div className="format-name">CSV</div>
                    <div className="format-desc">表格数据，可用Excel打开</div>
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="export-section">
            <h4>导出内容</h4>
            <div className="content-options">
              <label className="content-option">
                <input
                  type="radio"
                  name="content"
                  checked={exportOptions.sessionType === null}
                  onChange={() => toggleOption('sessionType', null)}
                />
                <span>全部标注</span>
              </label>

              <label className="content-option">
                <input
                  type="radio"
                  name="content"
                  checked={exportOptions.sessionType === 'guide'}
                  onChange={() => toggleOption('sessionType', 'guide')}
                />
                <span>📚 仅导读记录</span>
              </label>

              <label className="content-option">
                <input
                  type="radio"
                  name="content"
                  checked={exportOptions.sessionType === 'qa'}
                  onChange={() => toggleOption('sessionType', 'qa')}
                />
                <span>💬 仅问答记录</span>
              </label>

              <label className="content-option">
                <input
                  type="radio"
                  name="content"
                  checked={exportOptions.sessionType === 'manual'}
                  onChange={() => toggleOption('sessionType', 'manual')}
                />
                <span>📝 仅手动标注</span>
              </label>
            </div>
          </div>

          <div className="export-section">
            <h4>其他选项</h4>
            <label className="checkbox-option">
              <input
                type="checkbox"
                checked={exportOptions.includeDeleted}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggleOption('includeDeleted', e.target.checked)}
              />
              <span>包含已删除的标注</span>
            </label>
          </div>

          {error && (
            <div className="export-error">
              ❌ {error}
            </div>
          )}
        </div>

        <div className="export-footer">
          <button onClick={onClose} className="cancel-btn" disabled={exporting}>
            取消
          </button>
          <button onClick={handleExport} className="export-btn" disabled={exporting}>
            {exporting ? '导出中...' : '开始导出'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDialog;
